<template>
  <div class="catalog-seo-block flex flex-col gap-2">
    <div
      ref="text"
      v-resize-observer="onResizeObserver"
      class="catalog-seo-block__text"
      :class="{ 'line-clamp-6': !isFullView }"
      v-html="text"
    ></div>

    <button v-if="isTextClamped || isFullView" class="catalog-seo-block__button" @click="onToggleView">
      {{ buttonText }}
    </button>
  </div>
</template>

<script lang="ts" setup>
import { vResizeObserver } from '@vueuse/components'

import { isClamped } from '~/helpers'

const props = defineProps<{
  text: string
}>()

const isTextClamped = ref<boolean>(false)
const isFullView = ref<boolean>(false)
const textRef = useTemplateRef('text')

const buttonText = computed<string>(() => (isFullView.value ? 'Скрыть' : 'Подробнее'))

function onResizeObserver(entries: readonly ResizeObserverEntry[]) {
  const [entry] = entries

  if (!(entry.target instanceof HTMLElement) || isFullView.value) return

  isTextClamped.value = isClamped(entry.target)
}

function checkClampedText() {
  if (!import.meta.server) isTextClamped.value = isClamped(textRef.value)
}

function onToggleView() {
  isFullView.value = !isFullView.value
}

watch(() => props.text, checkClampedText, { immediate: true })
</script>

<style lang="scss">
.catalog-seo-block {
  color: #222736;
  margin: 80px 0 0 0;

  @media ($max-sm) {
    margin: 56px 0 0 0;
  }

  &__title {
    font-family: 'Canela', serif;
    font-size: 22px;
    font-style: normal;
    font-weight: 300;
    line-height: 28px;
    letter-spacing: 0.22px;
    text-transform: uppercase;
  }

  &__text {
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15px;
  }

  &__button {
    color: #a4a6ac;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15px;
    text-transform: uppercase;
    align-self: flex-start;
    margin-top: 16px;
  }

  &__content {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin-top: 40px;
      margin-bottom: 20px;
      font-weight: 300;
      font-size: 18px;
      font-family: Canela, serif;
      line-height: 24px;
      text-transform: uppercase;

      @media ($sm) {
        margin-top: 56px;
      }

      @media ($md) {
        width: calc(50% - 20px);
        margin-top: 80px;
        margin-left: auto;
      }
    }

    p {
      margin-bottom: 10px;

      @media ($md) {
        width: calc(50% - 20px);
        margin-left: auto;
      }
    }

    ul {
      @include listUl;
    }

    ol {
      margin-left: 20px;
      list-style: none;
      counter-reset: li;

      @media ($md) {
        margin-left: 36px;
      }

      & > li {
        position: relative;
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        &::before {
          position: absolute;
          top: 2px;
          display: inline-block;
          width: 32px;
          margin-left: -36px;
          padding-left: 20px;
          font-size: 13px;
          line-height: 16px;
          content: counters(li, '.') '. ';
          counter-increment: li;
          font-variant-numeric: lining-nums;
        }
      }
    }

    ul,
    ol {
      margin-top: 10px;

      @media ($md) {
        width: calc(50% - 20px);
        margin-left: auto;
        padding-left: 0;
      }
    }

    & .container {
      video {
        display: block;
        width: 100%;
        height: 100%;
        margin: 56px 0;
        object-fit: cover;

        @media ($sm) {
          margin: 80px auto;
        }
      }
    }

    & .container > .image {
      margin: 56px -16px;

      @media ($sm) {
        margin: 80px auto;
      }

      img {
        display: block;
        width: 100%;
        height: 100%;
        margin-bottom: 20px;
        object-fit: cover;

        @media ($sm) {
          margin: 0 0 40px;
        }
      }

      figcaption,
      a {
        display: block;
        margin: -4px 16px 0;
        font-size: 13px;
        line-height: 16px;

        @media ($sm) {
          margin-top: -20px;
          margin-left: 0;
        }
      }

      a {
        @media ($md) {
          cursor: pointer;
          transition: 0.3s;

          &:hover {
            color: $hoverColor;
            transition: 0.3s;
          }
        }
      }
    }
  }
}
</style>
